import { Controller } from "@hotwired/stimulus";
import { formSubmitWithWaitForJobCompletion } from "../../scripts/jobCompletion";
import * as Turbo from "@hotwired/turbo";
import "./CopyDataToBasePoi.scss";
import MapController from "../Map/MapController";
import { ITriggerZone } from '../../scripts/sharedModels';
import { IGpsCoordinates } from "../../scripts/_generated/IGpsCoordinates";

export default class CopyDataToBasePoiController extends Controller {
    static targets = [
        "baseName",
        "name",
        "selectedName",

        "baseBody",
        "body",
        "selectedBody",

        "baseBodyContentQuality",
        "bodyContentQuality",
        "selectedBodyContentQuality",

        "baseBodyAiGenerated",
        "bodyAiGenerated",
        "selectedBodyAiGenerated",

        "baseAuthor",
        "author",
        "selectedAuthor",

        "baseCopyright",
        "copyright",
        "selectedCopyright",

        "baseImagesSelected",
        "imagesSelected",

        "baseImage",
        "image",

        "selectedTriggerZones",

        "form",
        "submit",

        "baseAddress",
        "address",
        "selectedAddress",

        "baseOpeningHours",
        "openingHours",
        "selectedOpeningHours",

        "basePhone",
        "phone",
        "selectedPhone",

        "baseAdmission",
        "admission",
        "selectedAdmission",

        "baseWeb",
        "web",
        "selectedWeb",

        "baseExtraInfo",
        "extraInfo",
        "selectedExtraInfo",

        "baseExtraInfoAiGenerated",
        "extraInfoAiGenerated",
        "selectedExtraInfoAiGenerated",

        "selectedBaseAudio",
        "selectedAudio",

        "selectedBaseVideo",
        "selectedVideo",

        "selectedBaseCategory",
        "selectedCategory",

        "selectedBaseLabels",
        "selectedLabels",
    ];

    static values = {
        getJobUrl: String,
        baseCoordinates: Object,
        coordinates: Object,
        baseTriggerZones: Array,
        triggerZones: Array
    };

    readonly baseNameTarget!: HTMLElement;
    readonly nameTarget!: HTMLElement;
    readonly selectedNameTarget!: HTMLElement;

    readonly baseBodyTarget!: HTMLElement;
    readonly bodyTarget!: HTMLElement;
    readonly selectedBodyTarget!: HTMLElement;

    readonly baseBodyContentQualityTarget!: HTMLElement;
    readonly bodyContentQualityTarget!: HTMLElement;
    readonly selectedBodyContentQualityTarget!: HTMLElement;

    readonly baseBodyAiGeneratedTarget!: HTMLElement;
    readonly bodyAiGeneratedTarget!: HTMLElement;
    readonly selectedBodyAiGeneratedTarget!: HTMLElement;

    readonly baseAuthorTarget!: HTMLElement;
    readonly authorTarget!: HTMLElement;
    readonly selectedAuthorTarget!: HTMLElement;

    readonly baseCopyrightTarget!: HTMLElement;
    readonly copyrightTarget!: HTMLElement;
    readonly selectedCopyrightTarget!: HTMLElement;

    readonly baseImagesSelectedTarget!: HTMLElement;
    readonly imagesSelectedTarget!: HTMLElement;

    readonly baseImageTargets!: HTMLElement[];
    readonly imageTargets!: HTMLElement[];

    readonly selectedTriggerZonesTarget!: Element;

    readonly formTarget!: HTMLElement;
    readonly submitTarget!: HTMLElement;

    readonly getJobUrlValue!: string;
    readonly baseCoordinatesValue!: IGpsCoordinates;
    readonly coordinatesValue!: IGpsCoordinates;

    readonly baseTriggerZonesValue!: ITriggerZone[];
    readonly triggerZonesValue!: ITriggerZone[];

    readonly baseAddressTarget!: HTMLElement;
    readonly addressTarget!: HTMLElement;
    readonly selectedAddressTarget!: HTMLElement;

    readonly baseOpeningHoursTarget!: HTMLElement;
    readonly openingHoursTarget!: HTMLElement;
    readonly selectedOpeningHoursTarget!: HTMLElement;

    readonly basePhoneTarget!: HTMLElement;
    readonly phoneTarget!: HTMLElement;
    readonly selectedPhoneTarget!: HTMLElement;

    readonly baseAdmissionTarget!: HTMLElement;
    readonly admissionTarget!: HTMLElement;
    readonly selectedAdmissionTarget!: HTMLElement;

    readonly baseWebTarget!: HTMLElement;
    readonly webTarget!: HTMLElement;
    readonly selectedWebTarget!: HTMLElement;

    readonly baseExtraInfoTarget!: HTMLElement;
    readonly extraInfoTarget!: HTMLElement;
    readonly selectedExtraInfoTarget!: HTMLElement;

    readonly baseExtraInfoAiGeneratedTarget!: HTMLElement;
    readonly extraInfoAiGeneratedTarget!: HTMLElement;
    readonly selectedExtraInfoAiGeneratedTarget!: HTMLElement;

    readonly selectedBaseAudioTarget!: HTMLElement;
    readonly selectedAudioTarget!: HTMLElement;

    readonly selectedBaseVideoTarget!: HTMLElement;
    readonly selectedVideoTarget!: HTMLElement;

    readonly selectedBaseCategoryTarget!: HTMLElement;
    readonly selectedCategoryTarget!: HTMLElement;

    readonly selectedBaseLabelsTarget!: HTMLElement;
    readonly selectedLabelsTarget!: HTMLElement;

    connect() {
        formSubmitWithWaitForJobCompletion(
            $(this.formTarget),
            $(this.submitTarget),
            this.getJobUrlValue,
            () => {
                Turbo.clearCache();
                history.back();
            },
            "Really copy selected Place data to the Base Place?"
        );
    }

    cancel() {
        history.back();
    }

    baseNameSelected() {
        if (!this.baseNameTarget.nextSibling) throw new Error("Cannot read base POI name.");
        this.selectedNameTarget.textContent = this.baseNameTarget.nextSibling.textContent;
    }

    nameSelected() {
        if (!this.nameTarget.nextSibling) throw new Error("Cannot read POI name.");
        this.selectedNameTarget.textContent = this.nameTarget.nextSibling.textContent;
    }

    baseBodySelected() {
        if (!this.baseBodyTarget.nextSibling) throw new Error("Cannot read base POI description.");
        this.selectedBodyTarget.textContent = this.baseBodyTarget.nextSibling.textContent;
        this.selectedBodyContentQualityTarget.textContent = this.baseBodyContentQualityTarget.textContent;
        this.selectedBodyAiGeneratedTarget.textContent = this.baseBodyAiGeneratedTarget.textContent;
    }

    bodySelected() {
        if (!this.bodyTarget.nextSibling) throw new Error("Cannot read POI description.");
        this.selectedBodyTarget.textContent = this.bodyTarget.nextSibling.textContent;
        this.selectedBodyContentQualityTarget.textContent = this.bodyContentQualityTarget.textContent;
        this.selectedBodyAiGeneratedTarget.textContent = this.bodyAiGeneratedTarget.textContent;
    }

    baseAuthorSelected() {
        if (!this.baseAuthorTarget.nextSibling) throw new Error("Cannot read base POI author.");
        this.selectedAuthorTarget.textContent = this.baseAuthorTarget.nextSibling.textContent;
    }

    authorSelected() {
        if (!this.authorTarget.nextSibling) throw new Error("Cannot read POI author.");
        this.selectedAuthorTarget.textContent = this.authorTarget.nextSibling.textContent;
    }

    baseCopyrightSelected() {
        if (!this.baseCopyrightTarget.nextSibling) throw new Error("Cannot read base POI copyright.");
        this.selectedCopyrightTarget.textContent = this.baseCopyrightTarget.nextSibling.textContent;
    }

    copyrightSelected() {
        if (!this.copyrightTarget.nextSibling) throw new Error("Cannot read POI copyright.");
        this.selectedCopyrightTarget.textContent = this.copyrightTarget.nextSibling.textContent;
    }

    baseImagesSelected() {
        const areBaseImagesSelected = $(this.baseImagesSelectedTarget).is(":checked");
        this.baseImageTargets.forEach(x => $(x).prop("checked", areBaseImagesSelected));
    }

    imagesSelected() {
        const areImagesSelected = $(this.imagesSelectedTarget).is(":checked");
        this.imageTargets.forEach(x => $(x).prop("checked", areImagesSelected));
    }

    baseCoordinatesAndTriggerZonesSelected() {
        const mapController = this.getMapController();
        mapController.setMarkerCoordinates(this.baseCoordinatesValue);
        mapController.setTriggerZones(this.baseTriggerZonesValue);
    }

    coordinatesAndTriggerZonesSelected() {
        const mapController = this.getMapController();
        mapController.setMarkerCoordinates(this.coordinatesValue);
        mapController.setTriggerZones(this.triggerZonesValue);
    }

    private getMapController(): MapController {
        return this.application.getControllerForElementAndIdentifier(this.selectedTriggerZonesTarget, "map") as MapController;
    }

    baseAddressSelected() {
        if (!this.baseAddressTarget.nextSibling) throw new Error("Cannot read base POI address.");
        this.selectedAddressTarget.textContent = this.baseAddressTarget.nextSibling.textContent;
    }

    addressSelected() {
        if (!this.addressTarget.nextSibling) throw new Error("Cannot read POI address.");
        this.selectedAddressTarget.textContent = this.addressTarget.nextSibling.textContent;
    }

    baseOpeningHoursSelected() {
        if (!this.baseOpeningHoursTarget.nextSibling) throw new Error("Cannot read base POI opening hours.");
        this.selectedOpeningHoursTarget.textContent = this.baseOpeningHoursTarget.nextSibling.textContent;
    }

    openingHoursSelected() {
        if (!this.openingHoursTarget.nextSibling) throw new Error("Cannot read POI opening hours.");
        this.selectedOpeningHoursTarget.textContent = this.openingHoursTarget.nextSibling.textContent;
    }

    basePhoneSelected() {
        if (!this.basePhoneTarget.nextSibling) throw new Error("Cannot read base POI phone.");
        this.selectedPhoneTarget.textContent = this.basePhoneTarget.nextSibling.textContent;
    }

    phoneSelected() {
        if (!this.phoneTarget.nextSibling) throw new Error("Cannot read POI phone.");
        this.selectedPhoneTarget.textContent = this.phoneTarget.nextSibling.textContent;
    }

    baseAdmissionSelected() {
        if (!this.baseAdmissionTarget.nextSibling) throw new Error("Cannot read base POI admission.");
        this.selectedAdmissionTarget.textContent = this.baseAdmissionTarget.nextSibling.textContent;
    }

    admissionSelected() {
        if (!this.admissionTarget.nextSibling) throw new Error("Cannot read POI admission.");
        this.selectedAdmissionTarget.textContent = this.admissionTarget.nextSibling.textContent;
    }

    baseWebSelected() {
        if (!this.baseWebTarget.nextSibling) throw new Error("Cannot read base POI web.");
        this.selectedWebTarget.textContent = this.baseWebTarget.nextSibling.textContent;
    }

    webSelected() {
        if (!this.webTarget.nextSibling) throw new Error("Cannot read POI web.");
        this.selectedWebTarget.textContent = this.webTarget.nextSibling.textContent;
    }

    baseExtraInfoSelected() {
        if (!this.baseExtraInfoTarget.nextSibling) throw new Error("Cannot read base POI extra info.");
        this.selectedExtraInfoTarget.textContent = this.baseExtraInfoTarget.nextSibling.textContent;
        this.selectedExtraInfoAiGeneratedTarget.textContent = this.baseExtraInfoAiGeneratedTarget.textContent;
    }

    extraInfoSelected() {
        if (!this.extraInfoTarget.nextSibling) throw new Error("Cannot read POI extra info.");
        this.selectedExtraInfoTarget.textContent = this.extraInfoTarget.nextSibling.textContent;
        this.selectedExtraInfoAiGeneratedTarget.textContent = this.extraInfoAiGeneratedTarget.textContent;
    }

    baseAudioSelected() {
        this.selectedBaseAudioTarget.style.display = "block";
        this.selectedAudioTarget.style.display = "none";
    }

    audioSelected() {
        this.selectedBaseAudioTarget.style.display = "none";
        this.selectedAudioTarget.style.display = "block";
    }

    baseVideoSelected() {
        this.selectedBaseVideoTarget.style.display = "block";
        this.selectedVideoTarget.style.display = "none";
    }

    videoSelected() {
        this.selectedBaseVideoTarget.style.display = "none";
        this.selectedVideoTarget.style.display = "block";
    }

    baseCategorySelected() {
        this.selectedBaseCategoryTarget.style.display = "block";
        this.selectedCategoryTarget.style.display = "none";
    }

    categorySelected() {
        this.selectedBaseCategoryTarget.style.display = "none";
        this.selectedCategoryTarget.style.display = "block";
    }

    baseLabelsSelected() {
        this.selectedBaseLabelsTarget.style.display = "block";
        this.selectedLabelsTarget.style.display = "none";
    }

    labelsSelected() {
        this.selectedBaseLabelsTarget.style.display = "none";
        this.selectedLabelsTarget.style.display = "block";
    }
}
